import { useCallback, useMemo } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import ClientManagerService from '../services/ClientManager/clientManager.service';
import { ARCHIVE_GROUP_ID, ARCHIVE_GROUP_NAME } from '../services/ClientManager/clientManager.constants';
import { QueryKeys } from '../enums/queryKeys';
import type { ClientGroup } from '../types/ClientManagementTypes';

/** Prefix match, so callers never have to know the active search term. */
export const invalidateClientGroups = (queryClient: QueryClient) => queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_CLIENT_GROUPS] });

export const useClientGroups = (search: string) => {
  const queryClient = useQueryClient();
  const { getClientGroupsInfinite } = ClientManagerService();
  const { data, isLoading, isLoadingError, error, isFetchingNextPage, hasNextPage, fetchNextPage } = useInfiniteQuery({
    ...getClientGroupsInfinite(search),
  });

  // Memoised because every open panel re-renders off this array. The archived
  // clients of every page collapse into one group pinned after the active ones,
  // which is what makes it a single Archive group rather than one per page.
  const groups: ClientGroup[] = useMemo(() => {
    const pages = data?.pages ?? [];
    // The endpoint pages by client, so a group with clients on two pages arrives twice; merge it into one.
    // A client can also repeat when the page boundary shifts between fetches, so only unseen ones are added.
    const groupsById = new Map<number, ClientGroup>();
    pages
      .flatMap((page) => page.groups)
      .forEach((group) => {
        const existing = groupsById.get(group.id);
        if (!existing) {
          groupsById.set(group.id, group);
          return;
        }
        const seen = new Set(existing.clients.map((client) => client.id));
        groupsById.set(group.id, { ...existing, clients: [...existing.clients, ...group.clients.filter((client) => !seen.has(client.id))] });
      });
    const activeGroups = [...groupsById.values()];
    const archivedClients = pages.flatMap((page) => page.archivedClients);
    if (!archivedClients.length) return activeGroups;
    return [...activeGroups, { id: ARCHIVE_GROUP_ID, name: ARCHIVE_GROUP_NAME, clients: archivedClients }];
  }, [data]);

  // Stable: this reaches the panels via the page's action handlers, whose memo
  // it would otherwise defeat.
  const refreshGroups = useCallback(() => invalidateClientGroups(queryClient), [queryClient]);

  return {
    groups,
    refreshGroups,
    isLoadingGroups: isLoading,
    isGroupsLoadingError: isLoadingError,
    groupsError: error,
    isFetchingNextGroups: isFetchingNextPage,
    hasNextGroups: hasNextPage ?? false,
    fetchNextGroups: fetchNextPage,
  };
};
